import { DataService } from "@/services/data";
import { NodeTypeForm } from "@/types/dialer/node-type";
import { WorkflowLineage, WorkflowVersionSummary } from "@/types/dialer/workflow";
import { FieldDataType } from "@/types/dynamic-detail";

/**
 * The whole backend of the node editor, cached for the session by the browser. Shared
 * by the read-only version screen and the canvas editor, which live in two route trees
 * since the editor left the main layout: one loader, so a block type cannot appear on
 * one screen and be missing from the other.
 */
export async function loadNodeTypes(): Promise<NodeTypeForm[]> {
    const response = (await DataService.client.url(`/dialer/node-types/`).get().json()) as NodeTypeForm[] | { results: NodeTypeForm[] };
    return Array.isArray(response) ? response : (response.results ?? []);
}

/**
 * The six policies every node carries, derived server-side from `NodeBehavior`.
 * Separate from the node types because it does not depend on the type: one fetch
 * serves the behaviour section of every step, descriptions included.
 */
export async function loadBehaviorFields(): Promise<FieldDataType[]> {
    const response = (await DataService.client.url(`/dialer/behavior-fields/`).get().json()) as FieldDataType[] | { results: FieldDataType[] };
    return Array.isArray(response) ? response : (response.results ?? []);
}

/**
 * The declaration drawer's form, derived server-side from `VariableDeclaration`.
 * Same pattern as the behaviour fields: one model, one derived form, no component per
 * field — a type or a description added on the server appears here without a client change.
 */
export async function loadVariableFields(): Promise<FieldDataType[]> {
    const response = (await DataService.client.url(`/dialer/variable-fields/`).get().json()) as FieldDataType[] | { results: FieldDataType[] };
    return Array.isArray(response) ? response : (response.results ?? []);
}

/**
 * The URLs of this section carry the version *number* — `…/v/11` reads well and is
 * stable for a human — while every endpoint is keyed by the version *id*. The lineage
 * header is the mapping between the two, and it is also what the deploy dialog needs to
 * know which campaign slots a version could be deployed to, so the round trip is not
 * wasted.
 */
export async function loadLineage(code: string): Promise<WorkflowLineage> {
    return (await DataService.client.url(`/dialer/workflows/${code}/`).get().json()) as WorkflowLineage;
}

export async function resolveVersion(code: string, versionNumber: string | undefined): Promise<{ lineage: WorkflowLineage; version: WorkflowVersionSummary }> {
    const lineage = await loadLineage(code);
    const version = lineage.versions.find(candidate => String(candidate.version) === String(versionNumber));
    if (!version) {
        throw new Response(`Version ${versionNumber} of workflow ${code} does not exist.`, { status: 404 });
    }
    return { lineage, version };
}
